import { Box, Button, Flex, Icon, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { Tooltip } from "../components/Tooltip";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiMessageSquare, FiPlus, FiTrash2 } from "react-icons/fi";
import { useAssistantContext } from "./AssistantProvider";
import { ConversationRowDetail } from "./ConversationRowDetail";
import { useConversationActions } from "./useConversationActions";

const THUMB_TARGET = "44px";

export const ConversationSwitcher = ({ labelledAsConversations = false }: { labelledAsConversations?: boolean }) => {
  const { t } = useTranslation();
  const {
    summaries,
    hasOlderConversations,
    isLoadingOlderConversations,
    showOlderConversations,
    activeConversationId,
    isRunning,
  } = useAssistantContext();
  const {
    startNewConversation: leaveForNew,
    openConversation: leaveFor,
    confirmDelete,
    confirmationModal: modal,
  } = useConversationActions();

  const activeTitle =
    summaries.find((conversation) => conversation.id === activeConversationId)?.title ?? t("New conversation");

  return (
    <>
      <Menu placement="bottom-end" isLazy>
        <Tooltip label={labelledAsConversations ? undefined : activeTitle} isDisabled={labelledAsConversations}>
          <MenuButton
            as={Button}
            variant={labelledAsConversations ? "outline" : "ghost"}
            size="sm"
            leftIcon={labelledAsConversations ? <FiMessageSquare /> : undefined}
            rightIcon={<FiChevronDown />}
            isDisabled={isRunning}
            maxW={labelledAsConversations ? "52" : "full"}
            minW="0"
            flex="0 1 auto"
            h={labelledAsConversations ? THUMB_TARGET : { base: THUMB_TARGET, md: "8" }}
          >
            <Text isTruncated>{labelledAsConversations ? t("Conversations") : activeTitle}</Text>
          </MenuButton>
        </Tooltip>
        <MenuList maxH="sm" maxW="min(24rem, calc(100vw - 2rem))" overflowY="auto">
          <MenuItem icon={<FiPlus />} onClick={leaveForNew} isDisabled={isRunning}>
            {t("New conversation")}
          </MenuItem>
          {summaries.length > 0 && <MenuDivider />}
          {summaries.map((conversation) => (
            <MenuItem
              key={conversation.id}
              onClick={() => leaveFor(conversation.id)}
              isDisabled={isRunning}
              bg={conversation.id === activeConversationId ? "smBackgroundSecondary" : undefined}
              minH={labelledAsConversations ? THUMB_TARGET : undefined}
            >
              <Flex align="center" justify="space-between" gap="3" w="full">
                <Box flex="1" minW="0">
                  <Text fontSize="sm" color="smText" isTruncated>
                    {conversation.title}
                  </Text>
                  <ConversationRowDetail conversation={conversation} />
                </Box>
                <Tooltip label={t("Delete conversation")}>
                  <Box
                    as="span"
                    aria-label={t("Delete conversation") ?? "Delete conversation"}
                    color="smTextDisabled"
                    _hover={{ color: "smError" }}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    boxSize={labelledAsConversations ? THUMB_TARGET : undefined}
                    data-testid="delete-conversation"
                    onClick={(event) => {
                      event.stopPropagation();
                      confirmDelete(conversation.id, conversation.title);
                    }}
                  >
                    <Icon as={FiTrash2} />
                  </Box>
                </Tooltip>
              </Flex>
            </MenuItem>
          ))}
          {hasOlderConversations && (
            <MenuItem
              closeOnSelect={false}
              onClick={() => void showOlderConversations()}
              isDisabled={isLoadingOlderConversations}
              color="smPrimary"
              fontSize="sm"
            >
              {t("Show older conversations")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {modal}
    </>
  );
};
